'use client';

import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setTheme } from "@lib/features/theme/themeSlice";
import { getSystemTheme, isThemeMode, LEGACY_THEME_STORAGE_KEY, THEME_STORAGE_KEY } from "@/lib/theme";

export default function ThemeManager() {
    const dispatch = useDispatch();
    const mode = useSelector((state) => state.theme.mode);
    const preference = useSelector((state) => state.theme.preference);
    const initializedRef = useRef(false);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const stored = localStorage.getItem(THEME_STORAGE_KEY) || localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
        const documentTheme = document.documentElement.getAttribute("data-theme");
        const resolvedTheme = isThemeMode(stored)
            ? stored
            : (isThemeMode(documentTheme) ? documentTheme : getSystemTheme());

        document.documentElement.setAttribute("data-theme", resolvedTheme);
        document.documentElement.style.colorScheme = resolvedTheme;
        dispatch(setTheme({
            mode: resolvedTheme,
            preference: isThemeMode(stored) ? stored : "system",
        }));
        initializedRef.current = true;

        const handleSystemChange = () => {
            const stored = localStorage.getItem(THEME_STORAGE_KEY) || localStorage.getItem(LEGACY_THEME_STORAGE_KEY);

            if (!isThemeMode(stored)) {
                dispatch(setTheme({
                    mode: getSystemTheme(),
                    preference: "system",
                }));
            }
        };

        if (typeof mediaQuery.addEventListener === "function") {
            mediaQuery.addEventListener("change", handleSystemChange);
        } else {
            mediaQuery.addListener(handleSystemChange);
        }

        return () => {
            if (typeof mediaQuery.removeEventListener === "function") {
                mediaQuery.removeEventListener("change", handleSystemChange);
            } else {
                mediaQuery.removeListener(handleSystemChange);
            }
        };
    }, [dispatch]);

    useEffect(() => {
        if (!initializedRef.current || !isThemeMode(mode)) {
            return;
        }

        document.documentElement.setAttribute("data-theme", mode);
        document.documentElement.style.colorScheme = mode;

        if (preference === "system") {
            localStorage.removeItem(THEME_STORAGE_KEY);
        } else {
            localStorage.setItem(THEME_STORAGE_KEY, mode);
        }

        localStorage.removeItem(LEGACY_THEME_STORAGE_KEY);
    }, [mode, preference]);

    return null;
}
